import { formatExportDateAndTime } from 'utils/formatExportDateAndTime';
import { Asset } from 'components/common/types/Asset.types';
import { RouteFrameCode } from 'components/common/types/Deal.types';
import { FileList } from 'components/common/types';
import downloadSvg from 'assets/icons/download.svg';
import { useCallback, useMemo } from 'react';
import { download } from 'utils/download';
import { FRAME_LIST_LABELS, generateFrameListData, getFrameListSettings } from 'utils/frameList';
import xlsx from 'json-as-xlsx';

export const useFrameListExportActions = ({
  routeFrameCodes = [],
  listFiles = [],
  mapRouteFrameCodes = [],
  currencyCode,
  localeCode,
}: {
  routeFrameCodes?: RouteFrameCode[];
  listFiles?: FileList[];
  mapRouteFrameCodes?: RouteFrameCode[];
  currencyCode: string;
  localeCode: string;
}): { label: string; icon: string; onClick: () => void; disabled: boolean }[] => {
  const assets = useMemo(() => {
    const result: Partial<Asset>[] = [];

    routeFrameCodes.forEach(({ code: frameId, include }) => result.push({ frameId, include }));
    listFiles.forEach((listFile: FileList & { fileContents?: RouteFrameCode[] }) =>
      listFile.fileContents?.forEach(({ code: frameId, include }) => result.push({ frameId, include })),
    );
    mapRouteFrameCodes.forEach(({ code: frameId, include }) => result.push({ frameId, include }));

    return result;
  }, [routeFrameCodes, listFiles, mapRouteFrameCodes]);

  const generateXLSX = useCallback((): void => {
    const data = generateFrameListData({
      assets,
      currencyCode,
      localeCode,
      columns: [
        { label: FRAME_LIST_LABELS.frameId, value: 'frameId' },
        { label: FRAME_LIST_LABELS.include, value: 'include' },
      ],
    });
    xlsx(data, getFrameListSettings());
  }, [assets, currencyCode, localeCode]);

  const generateCSV = useCallback((): void => {
    const headers = ['Frame ID', 'Include'].join(',');
    const rows = assets.map((asset) => [asset.frameId, asset.include !== false].join(','));

    const csvBlob = new Blob([[headers, ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
    download(csvBlob, `frames-details_${formatExportDateAndTime(new Date())}.csv`);
  }, [assets]);

  return [
    {
      label: 'Export as XLSX',
      icon: downloadSvg,
      onClick: generateXLSX,
      disabled: !assets.length,
    },
    {
      label: 'Export as CSV',
      icon: downloadSvg,
      onClick: generateCSV,
      disabled: !assets.length,
    },
  ];
};
